import { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { stressStore, crisisLevel } from "../store";
import { makeGeometries, setRoot, poseHumanoid, flush } from "./humanoid";

const tmpColor = new THREE.Color();

// diverse global academic community
const SKIN = ["#f6d3b0", "#e8b98e", "#d69f6e", "#b87a45", "#8d5524", "#6b3f1d", "#ffe0bd", "#a5673f"];
const HOODIE = ["#2a9d8f", "#8e2b34", "#d9a441", "#3a4a63", "#556b2f", "#7b4b8a", "#c65d3b", "#455a64"];
const DENIM = ["#3b5b78", "#2f4a63", "#41566b", "#5a6b7a"];
const ROBE = ["#0b1b3a", "#141418", "#0f2547", "#1a1a20"];
const SUIT = ["#23262b", "#2c2f36", "#1b1e24", "#33373f"];

const rand = (arr) => arr[(Math.random() * arr.length) | 0];

function baseStress(s) {
  return Math.min(1, s.phd * 0.55 + s.funding * 0.55);
}

function setColor(mesh, i, hex) {
  if (!mesh) return;
  tmpColor.set(hex);
  mesh.setColorAt(i, tmpColor);
}

// Renders the shared set of body-part instanced meshes for one tier.
function BodyParts({ count, geo, refs, pack = false, cap = false }) {
  const bind = (key) => (m) => (refs.current[key] = m);
  const mat = (extra = {}) => (
    <meshStandardMaterial roughness={0.75} metalness={0.02} {...extra} />
  );
  return (
    <group>
      <instancedMesh ref={bind("legL")} args={[geo.leg, undefined, count]} frustumCulled={false} castShadow>
        {mat()}
      </instancedMesh>
      <instancedMesh ref={bind("legR")} args={[geo.leg, undefined, count]} frustumCulled={false} castShadow>
        {mat()}
      </instancedMesh>
      <instancedMesh ref={bind("torso")} args={[geo.torso, undefined, count]} frustumCulled={false} castShadow>
        {mat()}
      </instancedMesh>
      <instancedMesh ref={bind("armL")} args={[geo.arm, undefined, count]} frustumCulled={false} castShadow>
        {mat()}
      </instancedMesh>
      <instancedMesh ref={bind("armR")} args={[geo.arm, undefined, count]} frustumCulled={false} castShadow>
        {mat()}
      </instancedMesh>
      <instancedMesh ref={bind("head")} args={[geo.head, undefined, count]} frustumCulled={false} castShadow>
        {mat({ roughness: 0.55 })}
      </instancedMesh>
      {pack && (
        <instancedMesh ref={bind("pack")} args={[geo.pack, undefined, count]} frustumCulled={false} castShadow>
          {mat({ roughness: 0.9 })}
        </instancedMesh>
      )}
      {cap && (
        <instancedMesh ref={bind("cap")} args={[geo.cap, undefined, count]} frustumCulled={false} castShadow>
          {mat({ roughness: 0.5 })}
        </instancedMesh>
      )}
    </group>
  );
}

/* ---------- BASE (110): academic-casual, hustle walk, in/out of glass ----- */
function BaseCrowd({ count = 110 }) {
  const refs = useRef({});
  const geo = useMemo(() => makeGeometries({}), []);

  const agents = useMemo(
    () =>
      new Array(count).fill(0).map(() => ({
        baseR: 0.45 + Math.random() * 1.75,
        angle: Math.random() * Math.PI * 2,
        dir: Math.random() > 0.5 ? 1 : -1,
        radAmp: 0.35 + Math.random() * 0.7,
        radFreq: 0.4 + Math.random() * 0.9,
        radPhase: Math.random() * Math.PI * 2,
        jitter: 0,
        phase: Math.random() * Math.PI * 2,
        y: 0.06 + Math.random() * 0.28,
        h: 0.9 + Math.random() * 0.28, // height diversity
        b: 0.82 + Math.random() * 0.4, // build diversity
        skin: rand(SKIN),
        hoodie: rand(HOODIE),
        denim: rand(DENIM),
      })),
    [count]
  );

  useLayoutEffect(() => {
    const r = refs.current;
    agents.forEach((a, i) => {
      setColor(r.head, i, a.skin);
      setColor(r.torso, i, a.hoodie);
      setColor(r.armL, i, a.hoodie);
      setColor(r.armR, i, a.hoodie);
      setColor(r.legL, i, a.denim);
      setColor(r.legR, i, a.denim);
      setColor(r.pack, i, "#2b2b30");
    });
    Object.values(r).forEach((m) => m && m.instanceColor && (m.instanceColor.needsUpdate = true));
  }, [agents]);

  useFrame((s, dt) => {
    const st = stressStore.getState();
    const bs = baseStress(st);
    const crisis = crisisLevel(st);
    const t = s.clock.elapsedTime;
    const swell = 1 + crisis * 0.75;
    const speedMul = 0.7 + bs * 4.0;
    const scale = 0.26;

    for (let i = 0; i < count; i++) {
      const a = agents[i];
      a.jitter += (Math.random() - 0.5) * bs * dt * 6;
      a.jitter *= 0.9;
      a.angle += a.dir * dt * speedMul * 0.16 + a.jitter * dt;

      const breach = Math.sin(t * a.radFreq * (1 + bs) + a.radPhase) * a.radAmp * (1 + bs * 0.6);
      const r = (a.baseR + breach) * swell;
      const x = Math.cos(a.angle) * r;
      const z = Math.sin(a.angle) * r;

      // hustle walk cycle — faster & bigger stride under stress
      a.phase += dt * (5 + bs * 14) * Math.abs(a.dir);
      const stride = 0.3 + bs * 0.55;
      const legSwing = Math.sin(a.phase) * stride;
      const footY = a.y + Math.abs(Math.sin(a.phase)) * 0.02;

      setRoot(x, footY, z, a.angle + a.dir * Math.PI / 2, scale * a.b, scale * a.h, scale * a.b);
      poseHumanoid(refs.current, i, {
        legSwing,
        armSwing: Math.sin(a.phase) * (0.25 + bs * 0.45),
        lean: 0.1 + bs * 0.3,
        sway: Math.sin(a.phase * 0.5) * 0.04,
      });
    }
    flush(refs.current);
  });

  return <BodyParts count={count} geo={geo} refs={refs} pack />;
}

/* ---------- MIDDLE (30): doctoral regalia, stately stand ------------------ */
function TenureFaculty({ count = 30 }) {
  const refs = useRef({});
  const geo = useMemo(() => makeGeometries({ robe: true, legLen: 0.22 }), []);

  const agents = useMemo(
    () =>
      new Array(count).fill(0).map((_, i) => {
        const inner = i % 2 === 0;
        return {
          radius: inner ? 1.12 + Math.random() * 0.16 : 1.58 + Math.random() * 0.22,
          y: inner ? 2.16 : 1.18,
          angle: (i / count) * Math.PI * 2,
          speed: 0.1 + Math.random() * 0.05,
          seed: Math.random() * Math.PI * 2,
          h: 0.94 + Math.random() * 0.22,
          b: 0.9 + Math.random() * 0.3,
          skin: rand(SKIN),
          robe: rand(ROBE),
          hood: Math.random() > 0.5 ? "#1a5fd0" : "#0f9d68",
        };
      }),
    [count]
  );

  useLayoutEffect(() => {
    const r = refs.current;
    agents.forEach((a, i) => {
      setColor(r.head, i, a.skin);
      setColor(r.torso, i, a.robe);
      setColor(r.armL, i, a.robe);
      setColor(r.armR, i, a.robe);
      setColor(r.legL, i, "#14141a");
      setColor(r.legR, i, "#14141a");
      setColor(r.cap, i, a.hood);
    });
    Object.values(r).forEach((m) => m && m.instanceColor && (m.instanceColor.needsUpdate = true));
  }, [agents]);

  useFrame((s, dt) => {
    const t = s.clock.elapsedTime;
    const scale = 0.3;
    for (let i = 0; i < count; i++) {
      const a = agents[i];
      a.angle += a.speed * dt * 0.4; // slow, dignified drift
      const x = Math.cos(a.angle) * a.radius;
      const z = Math.sin(a.angle) * a.radius;
      // stately stand: minimal stride, gentle weight shift
      const legSwing = Math.sin(t * 1.5 + a.seed) * 0.05;
      setRoot(x, a.y, z, a.angle + Math.PI / 2, scale * a.b, scale * a.h, scale * a.b);
      poseHumanoid(refs.current, i, {
        legSwing,
        armSwing: 0.05,
        lean: 0,
        sway: Math.sin(t * 0.8 + a.seed) * 0.05,
      });
    }
    flush(refs.current);
  });

  return <BodyParts count={count} geo={geo} refs={refs} cap />;
}

/* ---------- APEX (10): corporate suits, slow executive stroll ------------- */
function AdminSuits({ count = 10 }) {
  const refs = useRef({});
  const geo = useMemo(() => makeGeometries({}), []);

  const agents = useMemo(
    () =>
      new Array(count).fill(0).map((_, i) => ({
        radius: 0.26 + (i % 3) * 0.12,
        angle: (i / count) * Math.PI * 2,
        speed: 0.03 + Math.random() * 0.03,
        phase: Math.random() * Math.PI * 2,
        y: 3.22 + (i % 2) * 0.24,
        h: 1.0 + Math.random() * 0.18,
        b: 0.92 + Math.random() * 0.24,
        skin: rand(SKIN),
        suit: rand(SUIT),
      })),
    [count]
  );

  useLayoutEffect(() => {
    const r = refs.current;
    agents.forEach((a, i) => {
      setColor(r.head, i, a.skin);
      setColor(r.torso, i, a.suit);
      setColor(r.armL, i, a.suit);
      setColor(r.armR, i, a.suit);
      setColor(r.legL, i, a.suit);
      setColor(r.legR, i, a.suit);
    });
    Object.values(r).forEach((m) => m && m.instanceColor && (m.instanceColor.needsUpdate = true));
  }, [agents]);

  useFrame((_, dt) => {
    const scale = 0.32;
    for (let i = 0; i < count; i++) {
      const a = agents[i];
      a.angle += a.speed * dt;
      a.phase += dt * 3 * a.speed * 20; // slow stroll cadence
      const x = Math.cos(a.angle) * a.radius;
      const z = Math.sin(a.angle) * a.radius;
      const legSwing = Math.sin(a.phase) * 0.18; // measured executive gait
      setRoot(x, a.y, z, a.angle + Math.PI / 2, scale * a.b, scale * a.h, scale * a.b);
      poseHumanoid(refs.current, i, {
        legSwing,
        armSwing: Math.sin(a.phase) * 0.12,
        lean: 0.02,
        sway: 0,
      });
    }
    flush(refs.current);
  });

  return <BodyParts count={count} geo={geo} refs={refs} />;
}

export default function NPCs() {
  return (
    <group>
      <BaseCrowd />
      <TenureFaculty />
      <AdminSuits />
    </group>
  );
}
